import {
  CompanyAccount,
  PersonAccount,
  WorkflowData,
  WorkflowEvent,
} from "../model";
import { Ajax } from "./infra";

/**
 * Methods for updating account information
 */
export class Accounts {

  /**
   * For NG1-dependency injection
   * @ignore
   */
  public static $inject = ["AjaxNg1"];

  constructor(private ajax: Ajax) {}

  /**
   * Gets the current company account if the current account is a company.
   * NOTE: Typically the account data is fetched in Session check => You do not need to fetch it separately.
   *
   * @returns A Promise with result data (Company account or null).
   */
  public getCompany(): Promise<CompanyAccount> {
    return this.ajax.getJSON("/accounts/company");
  }

  /**
   * Gets the current Person account:
   * Either the current account or the Primary contact person account if the current account is a company.
   * NOTE: Typically the current account data is fetched in Session check => You do not need to fetch it separately.
   * => Only use this to fetch the Primary contact person account if the current account is a company.
   *
   * @returns A Promise with result data (Personal account).
   */
  public getPerson(): Promise<PersonAccount> {
    return this.ajax.getJSON("/accounts/person");
  }

  /**
   * Saves the avatar and contact for the current Person account:
   * Either the current account or the Primary contact person account if the current account is a company.
   *
   * @returns A Promise with result data (Personal account).
   */
  public savePerson(personAccount: PersonAccount): Promise<PersonAccount> {
    return this.ajax.postJSON("/accounts/person", personAccount);
  }

  /**
   * Returns the url where to post the avatar file
   */
  public getAvatarUploadUrl(): string {
    return `${this.ajax.getApiAddress()}/accounts/avatar`;
  }

  /**
   * Switches the current web site usage role.
   *
   * @param role - household or worker. You cannot change to company at the moment using this method.
   *
   * @returns A Promise with result data (new role).
   */
  public switchRole(role: "worker" | "household"): Promise<"worker"|"household"> {
    return this.ajax.postJSON("/accounts/switchRole?role=" + role, {});
  }

  /**
   * Adds/updates the workflow event for the item.
   *
   * @param item - Item for which to set the workflow event.
   * @param wfEvent - Event to add or update.
   * @returns Workflow data for the item.
   */
  public saveWorkflowEvent(item: PersonAccount | CompanyAccount, wfEvent: WorkflowEvent): Promise<WorkflowData> {
    return this.ajax.postJSON(`/accounts/workflows`, wfEvent);
  }

  /**
   * Deletes the workflow item or items from the item.
   * @param item - Item for which to delete workflow events.
   * @param wfIdOrType - Event id or type.
   * @returns A Promise with result data "Object deleted".
   */
  public deleteWorkflowEvent(item: PersonAccount | CompanyAccount, wfIdOrType: string): Promise<string> {
    return this.ajax.remove(`/accounts/workflows/${wfIdOrType}`);
  }

  /**
   * Gets a single workflow event for the item.
   * @param item - Item for which to get the single event.
   * @param wfId - Event id.
   * @returns A Promise with event data.
   */
  public getWorkflowEvent(item: PersonAccount | CompanyAccount, wfId: string): Promise<WorkflowEvent> {
    return this.ajax.getJSON(`/accounts/workflows/events/${wfId}`);
  }
}